import { HalloweenArt } from "../../../../theme/HalloweenArt";
import { useEffect, useMemo, useRef, useState } from "react";
import OpenCafe from "./notify_modal/open_cafe";
import OrderingContent from "./order_content";
import { getCafeStatus } from "../../../../api/cafe.api";
import { getOrderByStatus, OrderResponse } from "../../../../api/order.api";

// Background poll interval. The previous live-update channel was a socket
// hook that's been stubbed to a no-op (no socket server in API v2), so
// silent polling is what keeps the page real-time without forcing the user
// to refresh.
const POLL_MS = 10_000;

type Status = "cafe-open" | "all-close";

const C = {
  espresso: "#5B3A29",
  caramel:  "#D97706",
  emerald:  "#059669",
  rose:     "#E11D48",
  amber:    "#F59E0B",
};
const HERO_GRAD = "linear-gradient(135deg,#FFFBEB 0%,#FEE2C7 45%,#FECACA 100%)";

const STATUS_OPTIONS: { value: Status; label: string; emoji: string }[] = [
  { value: "cafe-open", label: "Open",   emoji: "☕" },
  { value: "all-close", label: "Closed", emoji: "🔒" },
];

function Ordering() {
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<Status>("cafe-open");
  const [tempStatus, setTempStatus] = useState<Status>("cafe-open");
  const [initiate, setInitiate] = useState(true);
  const [isToggling, setIsToggling] = useState(false);
  const [ordered, setOrdered] = useState<OrderResponse[]>();
  const [doingOrder, setDoingOrder] = useState<OrderResponse[]>();
  const [readyOrder, setReadyOrder] = useState<OrderResponse[]>();
  // `initialLoading` only gates the spinner on the very first load — every
  // poll after that updates state silently so OrderingContent doesn't
  // unmount and reset its filter/scroll state on each refresh.
  const [initialLoading, setInitialLoading] = useState(true);
  const [oldSelected, setOldSelected] = useState("all");
  const fetchOrderRef = useRef<() => void>(() => {});

  const requestStatusChange = (next: Status) => {
    setIsToggling(true);
    setTempStatus(status);
    setStatus(next);
    setIsOpen(true);
  };

  const closeModal = (e: string) => {
    if (e === "close") setStatus(tempStatus);
    setIsOpen(false);
    setIsToggling(false);
  };

  const fetchData = async () => {
    try {
      const data = await getCafeStatus();
      if (initiate) {
        setStatus(data.isCafeOpen ? "cafe-open" : "all-close");
      }
      setInitiate(false);
      setIsToggling(false);
    } catch (e) {
      console.error("cafe status fetch", e);
    }
  };

  const fetchOrder = async () => {
    try {
      // No date args: the server applies its default window (last 7 days),
      // which is what the live queue has always effectively used.
      const incoming = await getOrderByStatus("ordered");
      const inProgress = await getOrderByStatus("in-progress");
      const doneOrders = await getOrderByStatus("done");
      setOrdered(incoming);
      setDoingOrder(inProgress);
      setReadyOrder(doneOrders);
    } catch (e) {
      console.error(e);
    } finally {
      setInitialLoading(false);
    }
  };

  // Keep the ref pointing at the latest fetchOrder so the interval closure
  // always sees the freshest version (avoids stale-closure pitfalls).
  useEffect(() => {
    fetchOrderRef.current = fetchOrder;
  });

  useEffect(() => {
    fetchData();
    fetchOrderRef.current();

    // Background polling — silent (no spinner, no remount), pauses while the
    // tab is hidden and immediately refetches when the tab comes back.
    let timer: number | null = null;
    const start = () => {
      if (timer != null) return;
      timer = window.setInterval(() => fetchOrderRef.current(), POLL_MS);
    };
    const stop = () => {
      if (timer != null) {
        window.clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchOrderRef.current();
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalLive = useMemo(() => {
    // @ts-ignore
    return (ordered?.length ?? 0) + (doingOrder?.length ?? 0) + (readyOrder?.length ?? 0);
  }, [ordered, doingOrder, readyOrder]);

  // @ts-ignore
  const incomingCount = ordered?.length ?? 0;
  // @ts-ignore
  const brewingCount = doingOrder?.length ?? 0;
  // @ts-ignore
  const readyCount = readyOrder?.length ?? 0;

  const isCafeOpen = status === "cafe-open";

  return (
    <>
      <OpenCafe isOpen={isOpen} onClose={closeModal} status={status} fetchData={fetchData} />

      <div className="min-h-screen font-sans" style={{ background: "#FAFAF7" }}>
        {/* ---------- Hero ---------- */}
        <header className="px-5 pt-[24px] pb-[16px]">
          <div
            className="rounded-3xl p-5 md:p-6 relative overflow-hidden"
            style={{ background: HERO_GRAD, boxShadow: "0 12px 36px -16px rgba(217,119,6,0.25)" }}
          >
            <div
              aria-hidden
              className="absolute -right-12 -top-12 w-[180px] h-[180px] rounded-full opacity-25"
              style={{ background: "radial-gradient(circle,#92400E 0%,transparent 70%)" }}
            />
            <p className="text-[11px] font-bold tracking-[0.12em] uppercase relative" style={{ color: C.espresso }}>
              <span className="seasonal-empty-icon">☕</span><HalloweenArt variant="witch-hat" className="art-staff" /> Barista station
            </p>
            <h1
              className="font-display font-black mt-2 leading-[0.95] tracking-tightest relative"
              style={{ color: "#1B0F08", fontSize: "clamp(34px,8vw,52px)" }}
            >
              {totalLive > 0
                ? `${totalLive} live order${totalLive > 1 ? "s" : ""}`
                : "All caught up"}
            </h1>
            <p className="font-semibold mt-1 relative" style={{ color: C.espresso, fontSize: "14px" }}>
              {isCafeOpen ? "Cafe is open · ready for orders" : "Cafe is closed"}
            </p>

            {/* Mini status row */}
            {isCafeOpen && totalLive > 0 && (
              <div className="mt-4 flex gap-2 flex-wrap relative">
                <MiniChip color="#2563EB" bg="#DBEAFE" emoji="⏳" label={`${incomingCount} waiting`} />
                <MiniChip color="#EA580C" bg="#FED7AA" emoji="🔥" label={`${brewingCount} brewing`} />
                <MiniChip color="#059669" bg="#D1FAE5" emoji="✓" label={`${readyCount} ready`} />
              </div>
            )}
          </div>
        </header>

        {/* ---------- Shop status toggle ---------- */}
        <div className="px-5 pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[11px] font-extrabold uppercase tracking-[0.10em]" style={{ color: C.espresso }}>
              Shop status
            </p>
            <div className="flex gap-2 p-1 rounded-2xl" style={{ background: "white", boxShadow: "0 0 0 1px #E7DFD2" }}>
              {STATUS_OPTIONS.map((opt) => {
                const active = status === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => !active && requestStatusChange(opt.value)}
                    disabled={isToggling}
                    className="inline-flex items-center gap-1.5 h-[34px] px-4 rounded-xl text-sm font-bold transition-all disabled:opacity-50"
                    style={
                      active
                        ? opt.value === "cafe-open"
                          ? { background: C.emerald, color: "#FFFFFF" }
                          : { background: "#1B0F08", color: "#FFFBEB" }
                        : { background: "transparent", color: C.espresso }
                    }
                  >
                    <span aria-hidden>{opt.emoji}</span>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ---------- Body ---------- */}
        <main className="px-5 pb-[40px]">
          {status === "all-close" ? (
            <div className="bg-white rounded-2xl py-[80px] flex flex-col items-center text-center" style={{ boxShadow: "0 1px 0 #ECE5D5" }}>
              <div className="w-[64px] h-[64px] rounded-full flex items-center justify-center mb-4 text-3xl" style={{ background: "#F3EBDA" }}>
                💤
              </div>
              <p className="font-display font-black text-lg" style={{ color: "#1B0F08" }}>Cafe is closed</p>
              <p className="text-sm mt-1" style={{ color: "#A38B6A" }}>Open the cafe to start receiving orders</p>
            </div>
          ) : initialLoading ? (
            <div className="py-[80px] flex justify-center">
              <svg className="w-6 h-6 animate-spin" fill="none" viewBox="0 0 24 24" style={{ color: C.caramel }}>
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25"/>
                <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            </div>
          ) : (
            <OrderingContent
              fetchOrder={fetchOrder}
              dataOrdered={ordered}
              dataInProgress={doingOrder}
              dataReady={readyOrder}
              oldSelected={oldSelected}
              setOld={setOldSelected}
            />
          )}
        </main>
      </div>
    </>
  );
}

const MiniChip: React.FC<{ color: string; bg: string; emoji: string; label: string }> = ({ color, bg, emoji, label }) => (
  <span
    className="inline-flex items-center gap-1.5 h-[28px] px-3 rounded-full text-[12px] font-bold"
    style={{ background: bg, color }}
  >
    <span aria-hidden>{emoji}</span>
    {label}
  </span>
);

export default Ordering;
